import { useRef, useCallback, useEffect } from "react";

type UseMultiClickActionArgs = {
  /** Number of clicks required to trigger the action */
  clicksRequired?: number;
  /** Time window in ms to complete all clicks */
  timeoutMs?: number;
  /** Action to execute when click count is reached */
  onAction: () => void;
};

export function useMultiClickAction({
  clicksRequired = 3,
  timeoutMs = 1000,
  onAction
}: UseMultiClickActionArgs) {
  const clickCountRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onActionRef = useRef(onAction);

  // Keep the ref up to date with the latest callback
  useEffect(() => {
    onActionRef.current = onAction;
  }, [onAction]);

  return useCallback(() => {
    clickCountRef.current += 1;

    if (clickCountRef.current === clicksRequired) {
      clickCountRef.current = 0;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      onActionRef.current();
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        clickCountRef.current = 0;
        timerRef.current = null;
      }, timeoutMs);
    }
  }, [clicksRequired, timeoutMs]);
}
